'use client'

import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Clock, Calendar, Edit, Trash2, Download, AlertTriangle, CheckCircle, History, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTimeEntries, useUpdateTimeEntry } from '@/hooks/queries'

interface TimeEntry {
  id: string
  user_id: string
  clock_in_time: string
  clock_out_time?: string
  total_hours?: number
  regular_hours?: number
  overtime_hours?: number
  status: string
  notes?: string
  is_corrected?: boolean
  corrected_by_role?: string
  correction_reason?: string
  is_deleted?: boolean
}

interface TimeEntriesListProps {
  staffId: string
  clinicId: string
  startDate?: string
  endDate?: string
  currentUserId: string
  currentUserName: string
  currentUserRole: string
  isAdmin?: boolean
}

export default function TimeEntriesList({
  staffId,
  clinicId,
  startDate,
  endDate,
  currentUserId,
  currentUserName,
  currentUserRole,
  isAdmin = false
}: TimeEntriesListProps) {
  const { t } = useTranslation('hr')
  // Use React Query for caching - data persists across navigation!
  const { 
    data: queryData, 
    isLoading: loading, 
    error: queryError,
    refetch,
    isFetching 
  } = useTimeEntries(staffId, clinicId, startDate, endDate)
  
  // Filter out deleted entries
  const entries = useMemo(() => {
    if (!queryData?.timeEntries) return []
    return queryData.timeEntries.filter((e: TimeEntry) => !e.is_deleted)
  }, [queryData?.timeEntries])
  
  const stats = queryData?.statistics || {
    totalHours: 0,
    regularHours: 0,
    overtimeHours: 0,
    daysWorked: 0
  }
  
  // Mutation for updating entries
  const updateTimeEntryMutation = useUpdateTimeEntry()
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  const [editClockIn, setEditClockIn] = useState('')
  const [editClockOut, setEditClockOut] = useState('')
  const [editReason, setEditReason] = useState('')
  const [saving, setSaving] = useState(false)
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deletingEntry, setDeletingEntry] = useState<TimeEntry | null>(null)
  const [deleteReason, setDeleteReason] = useState('')

  // Legacy function for compatibility with existing code
  const fetchTimeEntries = () => {
    refetch()
  }

  const formatDuration = (hours?: number) => {
    if (!hours) return '-'
    const h = Math.floor(hours)
    const m = Math.round((hours - h) * 60)
    return `${h}h ${m}m`
  }

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime)
    return {
      date: date.toLocaleDateString('sv-SE'),
      time: date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }),
      dateTimeLocal: date.toISOString().slice(0, 16) // For datetime-local input
    }
  }

  // Open edit modal
  const handleEditClick = (entry: TimeEntry) => {
    setEditingEntry(entry)
    const clockIn = formatDateTime(entry.clock_in_time)
    setEditClockIn(clockIn.dateTimeLocal)
    if (entry.clock_out_time) {
      const clockOut = formatDateTime(entry.clock_out_time)
      setEditClockOut(clockOut.dateTimeLocal)
    } else {
      setEditClockOut('')
    }
    setEditReason('')
    setEditModalOpen(true)
  }

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingEntry) return
    
    setSaving(true)
    try {
      // Determine if user is editing their own entry or someone else's
      const isOwnEntry = editingEntry.user_id === currentUserId
      const correctedByRole = isOwnEntry ? 'self' : currentUserRole

      const response = await fetch('/api/hr/time-tracking', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryId: editingEntry.id,
          clockInTime: new Date(editClockIn).toISOString(),
          clockOutTime: editClockOut ? new Date(editClockOut).toISOString() : null,
          correctedBy: currentUserId,
          correctedByName: currentUserName,
          correctedByRole: correctedByRole,
          correctionReason: editReason || t('timeEntries.editTitle')
        })
      })

      const data = await response.json()
      
      if (data.success) {
        setEditModalOpen(false)
        setEditingEntry(null)
        fetchTimeEntries() // Refresh list
        alert(t('timeEntries.updateSuccess'))
      } else {
        throw new Error(data.error || 'Failed to update')
      }
    } catch (error) {
      console.error('Error updating entry:', error)
      alert(t('timeEntries.updateError'))
    } finally {
      setSaving(false)
    }
  }

  // Open delete modal
  const handleDeleteClick = (entry: TimeEntry) => {
    setDeletingEntry(entry)
    setDeleteReason('')
    setDeleteModalOpen(true)
  }

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deletingEntry) return
    
    setSaving(true)
    try {
      const isOwnEntry = deletingEntry.user_id === currentUserId
      const deletedByRole = isOwnEntry ? 'self' : currentUserRole

      const params = new URLSearchParams({
        entryId: deletingEntry.id,
        deletedBy: currentUserId,
        deletedByName: currentUserName,
        deletedByRole: deletedByRole,
        reason: deleteReason || t('timeEntries.deleteConfirm')
      })

      const response = await fetch(`/api/hr/time-tracking?${params}`, {
        method: 'DELETE'
      })

      const data = await response.json()
      
      if (data.success) {
        setDeleteModalOpen(false)
        setDeletingEntry(null)
        fetchTimeEntries() // Refresh list
        alert(t('timeEntries.deleteSuccess'))
      } else {
        throw new Error(data.error || 'Failed to delete')
      }
    } catch (error) {
      console.error('Error deleting entry:', error)
      alert(t('timeEntries.deleteError'))
    } finally {
      setSaving(false)
    }
  }

  const exportToCSV = () => {
    const headers = ['Date', 'Clock In', 'Clock Out', 'Total Hours', 'Regular', 'Overtime', 'Corrected', 'Corrected By']
    const rows = entries.map(entry => {
      const clockIn = formatDateTime(entry.clock_in_time)
      const clockOut = entry.clock_out_time ? formatDateTime(entry.clock_out_time) : { date: '', time: '' }
      
      return [
        clockIn.date,
        clockIn.time,
        clockOut.time || '-',
        entry.total_hours?.toFixed(2) || '-',
        entry.regular_hours?.toFixed(2) || '-',
        entry.overtime_hours?.toFixed(2) || '-',
        entry.is_corrected ? 'Ja' : 'Nej',
        entry.corrected_by_role || '-'
      ]
    })

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `time-entries-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  // Check if user can edit/delete entries
  const canEdit = isAdmin || currentUserRole === 'admin' || staffId === currentUserId
  const canDelete = isAdmin || currentUserRole === 'admin'

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('timeEntries.total')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalHours.toFixed(1)}h</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('timeEntries.regular')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.regularHours.toFixed(1)}h</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('timeEntries.overtime')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {stats.overtimeHours.toFixed(1)}h
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('timeEntries.daysWorked')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.daysWorked}</div>
          </CardContent>
        </Card>
      </div>

      {/* Time Entries Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('timeEntries.title')}</CardTitle>
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              {t('timeEntries.export')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('timeEntries.date')}</TableHead>
                  <TableHead>{t('timeEntries.in')}</TableHead>
                  <TableHead>{t('timeEntries.out')}</TableHead>
                  <TableHead>{t('timeEntries.totalCol')}</TableHead>
                  <TableHead>{t('timeEntries.regularCol')}</TableHead>
                  <TableHead>{t('timeEntries.overtimeCol')}</TableHead>
                  <TableHead>{t('timeEntries.status')}</TableHead>
                  <TableHead className="text-right">{t('timeEntries.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      {t('timeEntries.noEntries')}
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => {
                    const clockIn = formatDateTime(entry.clock_in_time)
                    const clockOut = entry.clock_out_time ? formatDateTime(entry.clock_out_time) : null

                    return (
                      <TableRow key={entry.id} className={entry.is_corrected ? 'bg-yellow-50' : ''}>
                        <TableCell className="font-medium">{clockIn.date}</TableCell>
                        <TableCell>{clockIn.time}</TableCell>
                        <TableCell>{clockOut?.time || <Badge variant="outline">{t('timeEntries.ongoing')}</Badge>}</TableCell>
                        <TableCell>{formatDuration(entry.total_hours)}</TableCell>
                        <TableCell>{formatDuration(entry.regular_hours)}</TableCell>
                        <TableCell className="text-orange-600">
                          {formatDuration(entry.overtime_hours)}
                        </TableCell>
                        <TableCell>
                          {entry.is_corrected ? (
                            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                              <History className="h-3 w-3 mr-1" />
                              {t('timeEntries.corrected')}
                              {entry.corrected_by_role && (
                                <span className="ml-1 text-xs">
                                  ({entry.corrected_by_role === 'self' ? t('timeEntries.bySelf') : t('timeEntries.byRole', { role: entry.corrected_by_role })})
                                </span>
                              )}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t('timeEntries.original')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditClick(entry)}
                                title={t('timeEntries.edit')}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteClick(entry)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title={t('timeEntries.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              {t('timeEntries.editTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('timeEntries.editDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clockIn">{t('timeEntries.clockInLabel')}</Label>
              <Input
                id="clockIn"
                type="datetime-local"
                value={editClockIn}
                onChange={(e) => setEditClockIn(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="clockOut">{t('timeEntries.clockOutLabel')}</Label>
              <Input
                id="clockOut"
                type="datetime-local"
                value={editClockOut}
                onChange={(e) => setEditClockOut(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reason">{t('timeEntries.correctionReason')}</Label>
              <Textarea
                id="reason"
                placeholder={t('timeEntries.correctionPlaceholder')}
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                rows={2}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p className="font-medium">{t('timeEntries.correctionLog')}</p>
              <p className="text-xs mt-1">
                {t('timeEntries.correctionLogInfo', { name: currentUserName, role: (currentUserRole === 'self' || staffId === currentUserId ? t('timeEntries.selfCorrection') : currentUserRole) })}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              {t('timeEntries.cancel')}
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving || !editReason.trim()}>
              {saving ? t('timeEntries.saving') : t('timeEntries.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {t('timeEntries.deleteTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('timeEntries.deleteDescription')}
            </DialogDescription>
          </DialogHeader>
          
          {deletingEntry && (
            <div className="py-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p><strong>{t('timeEntries.dateLabel')}</strong> {formatDateTime(deletingEntry.clock_in_time).date}</p>
                <p><strong>{t('timeEntries.inLabel')}</strong> {formatDateTime(deletingEntry.clock_in_time).time}</p>
                <p><strong>{t('timeEntries.outLabel')}</strong> {deletingEntry.clock_out_time ? formatDateTime(deletingEntry.clock_out_time).time : t('timeEntries.notClockedOut')}</p>
                <p><strong>{t('timeEntries.totalTime')}</strong> {formatDuration(deletingEntry.total_hours)}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deleteReason">{t('timeEntries.deleteReason')}</Label>
                <Textarea
                  id="deleteReason"
                  placeholder={t('timeEntries.deletePlaceholder')}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <p className="font-medium">{t('timeEntries.deleteWarning')}</p>
                <p className="text-xs mt-1">
                  {t('timeEntries.deleteWarningInfo')}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t('timeEntries.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete} 
              disabled={saving || !deleteReason.trim()}
            >
              {saving ? t('timeEntries.deleting') : t('timeEntries.deleteConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
